// Starting a Facebook, LinkedIn or Google Analytics connection. Both the
// Analytics and the Connections page run it in a popup from the shared
// ConnectionDialog (useOAuthConnect), so the page stays where it is.
//
// The providers' consent pages can't be framed (both send frame-blocking
// headers), so a popup is the closest thing to "doing it in a dialog". The
// popup lands on the usual callback route (the redirect URIs registered with
// each provider), finishes the exchange there, and reports back over a
// BroadcastChannel, which — unlike window.opener — survives the cross-origin
// hops of an OAuth flow. A localStorage marker, written before the popup
// opens, tells the callback it is running in a popup and which run it belongs
// to; the callback also leaves the result in localStorage, for a page that
// wasn't listening when it arrived (takeOAuthResult). If the browser blocks the
// popup, the page can redirect instead and come back to the page and tab it
// left (setOAuthReturn / takeReturned).

import type { QueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { analyticsKeys } from '@/components/analytics/data/queries';
import type { AnalyticsPlatform, RefreshResult } from '@/components/analytics/data/types';
import { buildRedirectUri } from './oauth';
import { PROVIDER_OF } from './recheck';

export type OAuthProvider = 'facebook' | 'linkedin' | 'google';
/** The two pages that start a connect, and come back to it. */
export type OAuthView = 'analytics' | 'connections';
/** A first connect, or signing in again to a connection the brand has. */
export type ConnectMode = 'connect' | 'reconnect';

export interface OAuthPopupResult {
  nonce: string;
  provider: OAuthProvider;
  brandId: string;
  /** The platform whose dialog opened the popup, and whether it was a first
   *  connect (absent from popups an older build opened). */
  platform?: AnalyticsPlatform;
  mode?: ConnectMode;
  ok: boolean;
  /** The member closed the consent screen or denied access. */
  cancelled?: boolean;
  message?: string;
}

export interface PopupMarker {
  nonce: string;
  provider: OAuthProvider;
  /** What the callback can match: the run's own state (Facebook, LinkedIn
   *  and Google all send it back). */
  key: string;
  brandId: string;
  platform?: AnalyticsPlatform;
  mode?: ConnectMode;
  at: number;
}

const MARKER_KEY = 'maloo_oauth_popup';
const RESULT_KEY = 'maloo_oauth_result';
const RETURN_KEY = 'maloo_oauth_return';
const RETURNED_KEY = 'maloo_oauth_returned';
const CHANNEL = 'maloo-oauth';
const MARKER_TTL_MS = 30 * 60_000;
const RETURN_TTL_MS = 15 * 60_000;

// ── Consent URLs (one code path for the popup and the same-tab fallback) ─────

/** Facebook Login for Business: the permissions live in the login
 *  configuration (VITE_FACEBOOK_CONFIG_ID), not in this URL. */
function facebookConsentUrl(state: string): string {
  const appId = import.meta.env.VITE_FACEBOOK_APP_ID;
  const configId = import.meta.env.VITE_FACEBOOK_CONFIG_ID;
  if (!configId) throw new Error('Missing VITE_FACEBOOK_CONFIG_ID');
  return (
    'https://www.facebook.com/v24.0/dialog/oauth' +
    `?client_id=${appId}` +
    `&redirect_uri=${encodeURIComponent(buildRedirectUri('facebook'))}` +
    `&config_id=${configId}` +
    '&response_type=code' +
    `&state=${encodeURIComponent(state)}`
  );
}

/** A same-tab connect: the state is the brand id. */
export function facebookAuthUrl(brandId: string): string {
  return facebookConsentUrl(brandId);
}

/** A popup run: the state is "<brand id>.<run>". A popup the member closed
 *  leaves its marker behind, and a later same-tab connect for the brand — whose
 *  state is the bare brand id — must not be taken for that popup. */
export function facebookPopupAuth(brandId: string): { url: string; state: string } {
  const state = `${brandId}.${newNonce()}`;
  return { url: facebookConsentUrl(state), state };
}

/** The brand a Facebook state belongs to (brand ids hold no dot). */
export function brandFromFacebookState(state: string): string {
  return state.split('.')[0];
}

const LINKEDIN_SCOPES = [
  'w_organization_social',
  'rw_organization_admin',
  'r_organization_social',
  'r_organization_social_feed', // required for /rest/socialActions/{urn}/comments GET_ALL
  'r_organization_followers',
  'r_member_postAnalytics',
];

/** LinkedIn's consent URL. Stores the state, brand and redirect the callback
 *  checks — in localStorage, which a popup shares with the page. */
export function linkedInAuthUrl(brandId: string): { url: string; state: string } {
  const clientId = import.meta.env.VITE_LINKEDIN_CLIENT_ID;
  if (!clientId) throw new Error('LinkedIn Client ID not configured');
  for (const key of ['linkedin_oauth_state', 'linkedin_brand_id', 'linkedin_redirect_uri', 'linkedin_code_verifier', 'linkedin_client_id_last4']) {
    localStorage.removeItem(key);
  }
  const redirectUri = buildRedirectUri('linkedin');
  const state = Math.random().toString(36).substring(2) + Date.now().toString(36);
  localStorage.setItem('linkedin_oauth_state', state);
  localStorage.setItem('linkedin_brand_id', brandId);
  localStorage.setItem('linkedin_redirect_uri', redirectUri);
  const url =
    'https://www.linkedin.com/oauth/v2/authorization?' +
    'response_type=code&' +
    `client_id=${encodeURIComponent(clientId)}&` +
    `redirect_uri=${encodeURIComponent(redirectUri)}&` +
    `state=${encodeURIComponent(state)}&` +
    `scope=${encodeURIComponent(LINKEDIN_SCOPES.join(' '))}&` +
    'prompt=consent';
  return { url, state };
}

/** Google's consent URL comes from google-oauth-start. */
export async function googleAuthUrl(brandId: string): Promise<{ url: string; state: string }> {
  const { data, error } = await supabase.functions.invoke('google-oauth-start', {
    body: { brandId, redirectUri: buildRedirectUri('google') },
  });
  if (error) throw new Error(await functionErrorMessage(error, "Couldn't start Google sign-in."));
  if (!data?.authUrl) throw new Error('Missing authUrl');
  // A same-tab redirect checks this. A popup checks its marker instead: its
  // sessionStorage is a copy taken when it opened — before this was written.
  sessionStorage.setItem('google_oauth_state', data.state);
  return { url: data.authUrl, state: data.state };
}

/** What an edge function said when it answered with an error. supabase-js
 *  hands back its Response as error.context; the function's own message ("This
 *  Facebook login doesn't include …") beats "Edge Function returned a non-2xx
 *  status code". */
export async function functionErrorMessage(error: unknown, fallback: string): Promise<string> {
  const response = (error as { context?: { json?: () => Promise<unknown> } } | null)?.context;
  if (response && typeof response.json === 'function') {
    try {
      const body = (await response.json()) as Record<string, unknown> | null;
      const said = [body?.error_description, body?.error, body?.message].find((m) => typeof m === 'string' && m.trim());
      if (typeof said === 'string') return said;
    } catch {
      /* not JSON */
    }
    return fallback;
  }
  return error instanceof Error && error.message ? error.message : fallback;
}

// ── Asking the analytics pipeline to re-check ───────────────────────────────

/** Asks refresh-analytics-for-brand to re-check what a sign-in feeds (Facebook:
 *  Facebook and Instagram), for a connect whose page won't judge the answer
 *  itself. Says what the server answered: a platform it skipped ('recent',
 *  'in_flight', 'backing_off') wasn't re-checked by this call. Best effort:
 *  null on any error, and the numbers then wait for the server's next tick, as
 *  they would have anyway. */
export async function requestRecheck(provider: OAuthProvider, brandId: string): Promise<RefreshResult | null> {
  const platforms = (Object.keys(PROVIDER_OF) as AnalyticsPlatform[]).filter((p) => PROVIDER_OF[p] === provider);
  try {
    const answers = await Promise.all(platforms.map(async (platform) => {
      const { data, error } = await supabase.functions.invoke('refresh-analytics-for-brand', { body: { brandId, platform } });
      if (error) throw error;
      return data as RefreshResult;
    }));
    return { started: answers.flatMap((a) => a.started), skipped: answers.flatMap((a) => a.skipped) };
  } catch {
    return null;
  }
}

// ── The popup handshake ─────────────────────────────────────────────────────

function newNonce(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function readMarker(): PopupMarker | null {
  try {
    const marker = JSON.parse(localStorage.getItem(MARKER_KEY) ?? 'null') as PopupMarker | null;
    return marker && Date.now() - marker.at < MARKER_TTL_MS ? marker : null;
  } catch {
    return null;
  }
}

/** Only this run's: the member may have started over since. */
function dropMarker(nonce: string): void {
  if (readMarker()?.nonce === nonce) localStorage.removeItem(MARKER_KEY);
}

/** Opens the consent page in a popup — call it straight from the click
 *  handler, or the browser blocks it. Pass url null to open a blank popup and
 *  point it at the provider once its URL is known (Google). Null when the
 *  browser blocked the popup. */
export function openOAuthPopup(
  url: string | null,
  marker: { provider: OAuthProvider; key: string; brandId: string; platform?: AnalyticsPlatform; mode?: ConnectMode },
): { nonce: string; popup: Window } | null {
  const nonce = newNonce();
  localStorage.setItem(MARKER_KEY, JSON.stringify({ ...marker, nonce, at: Date.now() } satisfies PopupMarker));
  const popup = window.open(url ?? 'about:blank', 'maloo_oauth', 'popup,width=600,height=760');
  if (!popup) {
    localStorage.removeItem(MARKER_KEY);
    return null;
  }
  return { nonce, popup };
}

/** Sets the marker's key once it is known (Google's state arrives after the
 *  popup had to be opened). */
export function setPopupKey(nonce: string, key: string): void {
  const marker = readMarker();
  if (marker?.nonce === nonce) localStorage.setItem(MARKER_KEY, JSON.stringify({ ...marker, key }));
}

/** Gives up on a popup run before it reached the provider (Google's start
 *  failed): closes the blank window and forgets its marker. */
export function closeOAuthPopup(opened: { nonce: string; popup: Window }): void {
  opened.popup.close();
  dropMarker(opened.nonce);
}

// A run's result can reach this window several ways — the BroadcastChannel, a
// storage event, a look at localStorage on subscribe or when the tab is shown
// again, or takeOAuthResult — and is handled once, by whichever gets it first.
/** Runs whose result has been handed over in this window. */
const settled = new Set<string>();
/** Runs a dialog in this window is waiting for: takeOAuthResult leaves them to it. */
const listening = new Set<string>();

function readResult(): OAuthPopupResult | null {
  try {
    const result = JSON.parse(localStorage.getItem(RESULT_KEY) ?? 'null') as (OAuthPopupResult & { at: number }) | null;
    if (result && Date.now() - result.at < RETURN_TTL_MS) return result;
    if (result) localStorage.removeItem(RESULT_KEY);
  } catch {
    /* unreadable or storage blocked */
  }
  return null;
}

function dropResult(nonce: string): void {
  try {
    if (readResult()?.nonce === nonce) localStorage.removeItem(RESULT_KEY);
  } catch {
    /* storage blocked */
  }
}

/** The page's side: hears the result of the popup run `nonce` — over the
 *  BroadcastChannel, or from the copy the callback stored: on a storage event
 *  (a browser without BroadcastChannel), once on subscribe, and when the tab is
 *  focused or shown again (a frozen phone tab can miss both messages). */
export function onOAuthResult(nonce: string, handler: (result: OAuthPopupResult) => void): () => void {
  let stopped = false;
  const deliver = (result: OAuthPopupResult | null | undefined) => {
    if (stopped || result?.nonce !== nonce) return;
    dropResult(nonce);
    if (settled.has(nonce)) return;
    settled.add(nonce);
    handler(result);
  };
  const check = () => deliver(readResult());
  const onStorage = (event: StorageEvent) => {
    if (event.key === RESULT_KEY) check();
  };
  const onShown = () => {
    if (document.visibilityState === 'visible') check();
  };
  const channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL);
  if (channel) channel.onmessage = (event: MessageEvent<OAuthPopupResult>) => deliver(event.data);
  window.addEventListener('storage', onStorage);
  window.addEventListener('focus', check);
  document.addEventListener('visibilitychange', onShown);
  listening.add(nonce);
  // Not synchronously: the handler may stop this subscription, which the
  // caller only holds once this returns.
  queueMicrotask(check);
  return () => {
    stopped = true;
    listening.delete(nonce);
    channel?.close();
    window.removeEventListener('storage', onStorage);
    window.removeEventListener('focus', check);
    document.removeEventListener('visibilitychange', onShown);
  };
}

/** A popup result for this brand that no dialog in this window is waiting for
 *  (it was closed, or the page reloaded): read once, then gone. The opening
 *  page picks it up on mount and when it is focused or shown again. */
export function takeOAuthResult(brandId: string): OAuthPopupResult | null {
  const result = readResult();
  if (!result || result.brandId !== brandId || listening.has(result.nonce)) return null;
  dropResult(result.nonce);
  if (settled.has(result.nonce)) return null;
  settled.add(result.nonce);
  return result;
}

/** The callback's side: the marker when this callback runs in a popup that a
 *  page opened for this provider and key. */
export function popupMarkerFor(provider: OAuthProvider, key: string | null): PopupMarker | null {
  const marker = readMarker();
  return marker && key !== null && marker.provider === provider && marker.key === key ? marker : null;
}

/** Reports the outcome to the page that opened the popup, then closes it.
 *  Some browsers refuse window.close() after a cross-origin hop: the callback
 *  shows "you can close this window" for that case. */
export function finishOAuthPopup(marker: PopupMarker, result: { ok: boolean; cancelled?: boolean; message?: string }): void {
  const full: OAuthPopupResult = {
    nonce: marker.nonce, provider: marker.provider, brandId: marker.brandId, platform: marker.platform, mode: marker.mode, ...result,
  };
  // Stored before it is announced, so a page that hears the announcement finds
  // the copy to clear.
  try {
    localStorage.setItem(RESULT_KEY, JSON.stringify({ ...full, at: Date.now() }));
  } catch {
    /* storage blocked: the channel still carries it */
  }
  if (typeof BroadcastChannel !== 'undefined') {
    const channel = new BroadcastChannel(CHANNEL);
    channel.postMessage(full);
    channel.close();
  }
  dropMarker(marker.nonce);
  setTimeout(() => window.close(), 400);
}

// ── Same-tab fallback: come back to the page that started it ─────────────────

export interface OAuthReturn {
  /** The page to come back to; it reopens the dialog with the result. */
  view: OAuthView;
  /** The analytics channel tab to restore. */
  tab?: string;
  brandId: string;
  /** The analytics platforms to re-check once connected. */
  platforms: AnalyticsPlatform[];
  /** The sign-in it was left for: a callback for another channel or brand
   *  ignores it. */
  provider: OAuthProvider;
  /** The platform whose dialog started it. */
  platform: AnalyticsPlatform;
  mode: ConnectMode;
}

export function setOAuthReturn(back: OAuthReturn): void {
  sessionStorage.setItem(RETURN_KEY, JSON.stringify({ ...back, at: Date.now() }));
}

/** Forgets the return note: the same-tab sign-in that left it couldn't start,
 *  so the tab never left the page. */
export function clearOAuthReturn(): void {
  sessionStorage.removeItem(RETURN_KEY);
}

/** A note still within its 15 minutes, or null. */
function readNote<T>(key: string): T | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(key) ?? 'null') as (T & { at: number }) | null;
    return value && Date.now() - value.at < RETURN_TTL_MS ? value : null;
  } catch {
    return null;
  }
}

function dropNote(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* storage blocked */
  }
}

/** A note left by the build before this one, in the minutes around a deploy,
 *  came from the analytics page's Reconnect dialog and names only its
 *  platforms. */
function completed<T extends OAuthReturn>(note: T): T {
  return {
    ...note,
    view: note.view === 'connections' ? 'connections' : 'analytics',
    platform: note.platform ?? note.platforms[0],
    mode: note.mode ?? 'reconnect',
  };
}

/** Read once by a callback: where to go back to (else the Connections page).
 *  Only a return left for this provider and brand counts — one left by an
 *  abandoned attempt (Back on the consent screen) must not redirect a later
 *  connect. A null brand matches any brand (the callback failed before it knew
 *  which). */
export function takeOAuthReturn(from: { provider: OAuthProvider; brandId: string | null }): OAuthReturn | null {
  const back = readNote<OAuthReturn>(RETURN_KEY);
  dropNote(RETURN_KEY);
  if (!back || back.provider !== from.provider) return null;
  return from.brandId === null || back.brandId === from.brandId ? completed(back) : null;
}

export interface OAuthReturned extends OAuthReturn {
  /** The connect succeeded: re-check those platforms. */
  reconnected: boolean;
}

/** Left by a callback that returns to the page that started it, for that page
 *  to read when it opens: the result to show, and which brand it was on (the
 *  reload restores only a sub-brand's parent). */
export function noteReturned(back: OAuthReturned): void {
  sessionStorage.setItem(RETURNED_KEY, JSON.stringify({ ...back, at: Date.now() }));
}

/** Read once, by the page the note is for; another page's note is left for it. */
export function takeReturned(view: OAuthView = 'analytics'): OAuthReturned | null {
  const raw = readNote<OAuthReturned>(RETURNED_KEY);
  const note = raw && completed(raw);
  if (note && note.view !== view) return null;
  dropNote(RETURNED_KEY);
  return note;
}

/** Where a callback sends the tab when it is done (not in a popup): back to
 *  the page in the return note, which takes the result from there — else to the
 *  Connections page, asking for the re-check itself so the numbers don't wait
 *  for the server's next tick. The page it lands on can read the brand before
 *  the server has claimed that run, and only a read that shows the run's lease
 *  polls to its end: so the brand is read again once the server has answered,
 *  on the app's QueryClient (the callback routes sit inside it). */
export function leaveCallback(
  navigate: (to: string, opts: { replace: boolean }) => void,
  ok: boolean,
  from: { provider: OAuthProvider; brandId: string | null },
  queryClient: QueryClient,
): void {
  const back = takeOAuthReturn(from);
  const brandId = from.brandId;
  if (back) noteReturned({ ...back, reconnected: ok });
  else if (ok && brandId) {
    void requestRecheck(from.provider, brandId)
      .then(() => queryClient.invalidateQueries({ queryKey: analyticsKeys.brandRoot(brandId) }));
  }
  sessionStorage.setItem('navigate_to_view', back?.view ?? 'connections');
  navigate('/', { replace: true });
}
